import type { ContactOrganizationValue } from './OrganizeContact.js';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { CrmTagVocabulary } from './crmTagSelection.js';
import type { ReviewView } from './navigation.js';
import type { PhoneDeliveryLine, PhoneType } from './phoneList.js';

export type { CrmTagVocabulary, CrmTagOption } from './crmTagSelection.js';
export type { PhoneDeliveryLine, PhoneType } from './phoneList.js';
/** Where a phone's type came from: a printed label, the number plan, a CSV column, a person, or nothing. */
export type PhoneTypeEvidence = 'label' | 'number_plan' | 'column' | 'human' | 'none';

/** Reads the CSRF cookie for the double-submit header on every state change. */
function csrfToken(): string {
  const m = document.cookie.match(/(?:^|;\s*)signal_csrf=([^;]+)/);
  return m?.[1] ? decodeURIComponent(m[1]) : '';
}

export interface Me { userId: string; workspaceId: string | null; role: string | null; email: string | null }
export interface Workspace { id: string; name: string; policy_mode: 'calibration' | 'enforcing' }
export interface WorkspaceSummary { id: string; name: string; role: string; active: boolean }

export type ConnectorStatus =
  | 'NOT_CONNECTED' | 'PENDING_VERIFICATION' | 'CONNECTED'
  | 'REAUTH_REQUIRED' | 'ERROR' | 'DISCONNECTED' | 'HISTORICAL_RETAINED'
  | 'NOT_CONFIGURED' | 'COMING_SOON';

export interface ConnectorCapability {
  key: 'read_marked' | 'list_markers' | 'create_markers';
  label: string;
  granted: boolean;
  optional: boolean;
  detail: string | null;
  upgrade: 'labelManagement' | 'categoryManagement' | null;
}

export interface Connector {
  kind: string;
  name: string;
  status: ConnectorStatus;
  /**
   * Whether mail is currently flowing, independent of whether we can authenticate.
   *
   * `status` answers "must I do something"; this answers "is it working right now".
   * Collapsing them is what made a rate-limited Gmail mailbox render as disconnected,
   * complete with a Connect button, while its grant was perfectly valid.
   */
  sync?: {
    state: 'healthy' | 'syncing' | 'delayed' | 'blocked' | 'idle';
    lastSuccessfulSyncAt: string | null;
    lastErrorCategory: string | null;
    retryAt: string | null;
  };
  /**
   * Whether this provider can be connected right now.
   *
   * Stage 1 gives a workspace one mailbox, so the other provider is unavailable until the
   * current one is explicitly disconnected. False here disables the control — it does not
   * hide the tile, because Outlook being supported and Outlook being available today are
   * different facts and the customer needs both.
   */
  canConnect?: boolean;
  blockedReason?: string | null;
  /**
   * Whether there is a live mailbox for Disconnect to act on. Server-computed.
   *
   * Not inferred from `status`: a leftover mailbox row could produce REAUTH_REQUIRED with
   * no slot behind it, and the page offered Disconnect for something disconnect could not
   * reach.
   */
  canDisconnect?: boolean;
  /** Mailboxes still holding stored mail that are NOT this connector's connection. */
  historical?: {
    emailAccountId: string;
    addressNormalized: string;
    messages: number;
    threads: number;
    classified: number;
    complianceHold: number;
    disconnectedAt: string | null;
    /** Null when no deletion clock is running. Shown as such, never invented. */
    retainedUntil: string | null;
  }[];
  /**
   * Mailbox connectors only: what the grant allows, in the customer's words, and which
   * optional capability can be added to the SAME connection (`upgrade` names the
   * start-flow flag). Absent when nothing is connected.
   */
  capabilities?: ConnectorCapability[];
  available: boolean;
  connectionId: string | null;
  credentialKind: string | null;
  lastVerifiedAt: string | null;
  lastErrorCode: string | null;
  canVerify: boolean;
  canMapTags: boolean;
  plannedIn: string | null;
  providerAccountId?: string | null;
  mailbox?: {
    messages: number;
    threads: number;
    attachments: number;
    queued: number;
    lastSyncedAt: string | null;
    syncState: string;
    ingestSince: string | null;
    hasCursor: boolean;
    aiProcessing: 'active' | 'awaiting_activation' | 'no_ai_credential';
    messagesAwaitingActivation: number;
    /** Messages in the canonical held state, not merely stored. */
    complianceHold: number;
    processing: number;
    classified: number;
    failed: number;
  } | null;
}

/** One item in the email review queue. Nothing model-internal, nothing provider-specific. */
/**
 * A message that finished Email Intelligence, whether or not anybody must act on it.
 *
 * Distinct from ReviewItem: this is the OBSERVATION surface. It carries no approval
 * controls and no editable CRM state — the Review queue remains the only place anything
 * is decided.
 */
export interface ClassifiedMessage {
  intelligenceId: string;
  messageId: string | null;
  sender: string;
  subject: string | null;
  receivedAt: string | null;
  processingState: string;
  failureCode: string | null;
  relationship: { value: string | null; confidence: number | null; source: string | null };
  emailLabelingEnabled: boolean;
  emailLabels: Array<{ key: string; confidence: number }>;
  reviewTaskId: string | null;
  crmProposal: { operation: string; state: string } | null;
}

export interface ReviewItem {
  id: string;
  intelligenceId: string;
  messageId: string | null;
  origin: string;
  sender: string;
  senderName: string | null;
  subject: string | null;
  summary: string | null;
  receivedAt: string | null;
  relationship: { value: string | null; confidence: number | null; source: string | null };
  modelRelationship: string | null;
  /**
   * What the identity resolver concluded, kept separate from the relationship.
   *
   * "Who is this person to us" and "which contact record is them" are different
   * questions. The card used to answer the second with the first, which is how it came
   * to claim CRM authority for a sender it could not name.
   */
  identity: {
    resolved: boolean;
    externalContactId: string | null;
    basis: 'crm_mapping' | 'local_identity' | 'none';
  };
  /** The one reason to lead with in a queue row. The rest stay in `reviewReasons`. */
  primaryReason: string | null;
  /** Message-level Email Labels. Never CRM tags — the two are separate fields. */
  emailLabels: Array<{ key: string; confidence: number }>;
  /** False means labelling was off for this classification, not that it found nothing. */
  emailLabelingEnabled: boolean;
  /** The CRM operation's tag keys. Empty when there is no proposal. */
  crmTags: string[];
  rejectedTags: Array<{ key: string; reason: string }>;
  reviewReasons: string[];
  confidence: number | null;
  modelTier: string | null;
  hasAttachment: boolean;
  proposedOperation: {
    id: string; operation: string; tagKeys: string[]; state: string; staleReason: string | null;
  } | null;
  status: string;
  createdAt: string;
}

/**
 * Everything the View Message screen shows.
 *
 * `body` is the normalized text stored at ingestion — the same text the classifier was
 * given — not a fresh fetch from the provider. No attachment bytes, no thread history.
 */
export interface MessageView {
  message: {
    id: string;
    sender: string;
    senderName: string | null;
    to: string[];
    cc: string[];
    subject: string | null;
    receivedAt: string | null;
    direction: string;
    body: string | null;
    bodyTruncated: boolean;
    attachmentCount: number;
  };
  classification: {
    intelligenceId: string;
    processingState: string;
    failureCode: string | null;
    relationship: { value: string | null; confidence: number | null; source: string | null };
    modelRelationship: string | null;
    /** Why the sender wrote. `source` distinguishes a header fact from a model answer. */
    intent: { key: string; confidence: number; source: string | null } | null;
    emailLabelingEnabled: boolean;
    emailLabels: Array<{ key: string; confidence: number }>;
    reviewReasons: string[];
    modelTier: string | null;
    identity: {
      resolved: boolean;
      externalContactId: string | null;
      basis: 'crm_mapping' | 'local_identity' | 'none';
    };
  } | null;
  crmProposal: { id: string; operation: string; tagKeys: string[]; state: string } | null;
  reviewTaskId: string | null;
  feedback: Array<{
    dimension: string; labelKey: string | null; decision: string; correctedValue: string | null;
  }>;
  crmWriteEnabled: boolean;
}

/** What a bounded read actually did. Counts, never content. */
export interface SyncReport {
  mode: 'initial' | 'incremental' | 'recovering';
  /** False when no capture marker is configured: the sync read nothing. */
  captureConfigured?: boolean;
  markerRemoved?: number;
  listed: number;
  alreadyHeld: number;
  fetched: number;
  inserted: number;
  threadsTouched: number;
  attachmentsRecorded: number;
  queued: number;
  skipped: number;
  recoveredFromExpiredCursor: boolean;
  /**
   * Messages the provider reported as gone. Only Outlook's delta feed reports these;
   * Gmail's history is asked only about additions, so it is absent there rather than
   * zero — and the UI says nothing about removals it was never told about.
   */
  removed?: number;
  /** Named by the provider, then gone before it could be fetched. Both providers. */
  vanished?: number;
}

/**
 * Mirrors TAG_CATEGORIES in @signal/domain-policy.
 *
 * `email` was missing here long after migration 033 added it, so the client could not
 * name the category it needed to filter on — and the root typecheck did not catch it
 * because the web package builds under its own tsconfig. The web build did.
 */
export type TagCategory =
  'relationship' | 'relationship_type' | 'provenance' | 'email' | 'intent' | 'topic' | 'other';

export interface TagMappingRow {
  canonicalKey: string;
  label: string;
  group: string;
  externalTagId: string | null;
  displayName: string | null;
  enabled: boolean;
  category: TagCategory | null;
  aiAllowed: boolean;
  autoApplyAllowed: boolean;
  reviewRequired: boolean;
  aiGuidance: string | null;
}

/** A CRM tag an administrator approved for the classifier to choose from. */
export interface ApprovedTagRow {
  canonicalKey: string;
  externalTagId: string;
  displayName: string | null;
  category: TagCategory;
  origin: 'core' | 'admin';
  enabled: boolean;
  aiAllowed: boolean;
  autoApplyAllowed: boolean;
  reviewRequired: boolean;
  aiGuidance: string | null;
}

export interface CategoryRule { category: TagCategory; cardinality: 'single' | 'multi' }

export interface KeapTag { externalId: string; name: string }

export interface IntakeBatch {
  id: string;
  source: string;
  status: string;
  label: string | null;
  itemCount: number;
  createdAt: string;
}

/**
 * Where an import has actually got to.
 *
 * Every number is a count of rows. There is deliberately no percentage from the server:
 * a percentage is a claim about time remaining, and this knows how many rows are left,
 * not how slow the next CRM lookup will be. A screen may divide and say so.
 */
export interface IntakeProgress {
  total: number;
  queued: number;
  processing: number;
  resolved: number;
  ready: number;
  needsReview: number;
  duplicate: number;
  invalid: number;
  skipped: number;
  failed: number;
  cancelled: number;
  approved: number;
  rejected: number;
  /** Rows waiting in Review only because no CRM could be asked about them yet. */
  awaitingCrm: number;
  running: boolean;
  cancelRequested: boolean;
  startedAt: string | null;
  finishedAt: string | null;
}

/** One contact waiting for a person, whatever source produced it. */
export interface IntakeReviewItem {
  taskId: string;
  itemId: string;
  source: string;
  batchId: string;
  batchLabel: string | null;
  status: string;
  name: string | null;
  company: string | null;
  email: string | null;
  relationship: string | null;
  tagKeys: string[];
  reviewReasons: string[];
  errorCode: string | null;
  planAction: string | null;
  createdAt: string;
}

export interface IntakeItemDetail {
  item: IntakeItem & {
    plan: {
      action: string;
      relationship: string | null;
      tagKeys: string[];
      fieldChanges: Array<{ field: string; current: unknown; proposed: unknown }>;
      reason: string;
    } | null;
    planHash: string | null;
    approvalState: 'approved' | 'rejected' | null;
    reviewReasons: string[];
    decision: string | null;
  };
  /** What will happen to each number, before anything is sent. */
  phones?: PhoneDeliveryLine[];
  allowedTagKeys: string[];
  relationships: string[];
  /** The CRM's contact types; and, for an existing contact, the type the CRM records. */
  contactTypes?: CrmContactTypeOptions;
  existingContactType?: string | null;
  /** Where an approval could go: a CRM, and how many active Zapier or webhook destinations. Absent from an older server. */
  delivery?: { crmConnected: boolean; activeDestinations: number };
  /**
   * The existing Keap contact whose Contact Type a person may change from this row (its
   * update target, or the contact it matched with nothing else to send), with Keap's live
   * value. Null when there is none, or when the match was in doubt.
   */
  existingContact?: { externalContactId: string; status: 'ok' | 'unavailable'; contactType: string | null } | null;
  /** The row's email is a shared address: confirmed or not, and what Keap holds. Absent from an older server. */
  sharedAddress?: import('./SharedAddress.js').SharedAddressState | null;
  crmWriteEnabled: boolean;
}

export interface IntakeItem {
  id: string;
  position: number;
  source: string;
  status: string;
  sourceRef: string | null;
  relationship: string | null;
  tagKeys: string[];
  errorCode: string | null;
  identityResolved: boolean;
  planHash?: string | null;
  approvalState?: 'approved' | 'rejected' | null;
  candidate: {
    fullName: string | null;
    givenName: string | null;
    familyName: string | null;
    company: string | null;
    emails: Array<{ normalized: string }>;
    organization?: ContactOrganizationValue | null;
    /** Every number, in the source's order, each with its type. */
    phones: Array<{
      e164: string | null; raw: string; type?: PhoneType; confidence?: number;
      label?: string | null; typeEvidence?: PhoneTypeEvidence;
    }>;
  } | null;
}

export interface IntakeSettings {
  defaultPhoneRegion: string | null;
  /**
   * The ONE optional default CRM tag for image and file capture: a stage-group tag here,
   * or one other tag in `defaultCrmTagKeys` (at most one entry), never both.
   */
  defaultCommercialStage: CaptureCommercialStage | null;
  defaultCrmTagKeys: string[];
  emailLabelingEnabled: boolean;
  regions: Array<{ code: string; label: string }>;
}

export interface CsvSuggestion {
  columnIndex: number;
  header: string;
  field: string | null;
  confidence: 'exact' | 'alias' | 'ambiguous' | 'none';
  alternatives: string[];
}

export interface CsvUploadResult {
  batch: IntakeBatch;
  alreadyExists?: boolean;
  headers: string[];
  suggestions: CsvSuggestion[];
  fields: Array<{ field: string; label: string }>;
  totalRows: number;
  truncated: boolean;
  /** Rows an import would record. Fewer than totalRows only when the file was cut. */
  importableRows?: number;
  /** Set when the whole file will be refused: it is over this workspace's per-file limit. */
  /** totalRows is null when the file runs past what the parser reads: its length is unknown. */
  overLimit?: { limit: number; totalRows: number | null } | null;
  sample: Array<{ rowNumber: number; values: string[]; error: string | null }>;
}

export interface ChoosableTag {
  canonicalKey: string;
  displayName: string;
  category: TagCategory;
  aiAllowed: boolean;
  autoApplyAllowed: boolean;
  reviewRequired: boolean;
  eligible: boolean;
}

export interface RelationshipAssociationRow {
  relationshipKey: string;
  relationshipTag: { canonicalKey: string; displayName: string; mapped: boolean };
  additionalTagKeys: string[];
  additionalTagCount: number;
}


/* ── Destinations: approved contacts to Zapier and webhooks ───────────────── */

export interface DestinationOrganizationView { leadSource?: string | null; pipeline?: string | null; stage?: string | null; tags?: string[] }
export interface DestinationView {
  id: string; kind: 'webhook' | 'zapier'; name: string; url: string; status: 'active' | 'disabled' | 'revoked';
  organization: DestinationOrganizationView; lastDeliveryAt: string | null; lastErrorCode: string | null; createdAt: string;
}
export interface DeliveryRow {
  id: string; destinationId: string; eventPublicId: string; contactName: string | null;
  status: 'pending' | 'delivered' | 'failed' | 'gone'; attemptCount: number; lastStatusCode: number | null;
  lastErrorCode: string | null; deliveredAt: string | null; createdAt: string;
}
export interface ZapierKeyView { id: string; name: string; prefix: string; lastUsedAt: string | null; revokedAt: string | null; createdAt: string }
export interface CompanionConnection { id: string; name: string; mine: boolean; person: string | null; lastUsedAt: string | null; createdAt: string }
export interface DestinationsResponse {
  destinations: DestinationView[]; zapierKeys: ZapierKeyView[]; deliveries: DeliveryRow[]; sample: Record<string, unknown>;
}

export interface Overview {
  contactsKnown: number;
  crmOperations: number;
  openReviews: number;
  approvedTagMappings: number;
  /** CRM operation outcomes for the Dashboard. Absent from an older server. */
  crm?: { succeeded: number; failed: number; pending: number; contactsCreatedLast7Days: number };
}

export interface ActivityEvent {
  /** Present with `?scope=customer`: the event in the customer's words. */
  title?: string;
  kind?: 'card' | 'email' | 'csv' | 'crm' | 'problem';
  id: string;
  occurredAt: string;
  action: string;
  subjectType: string;
  outcome: string;
  errorCode: string | null;
  detail: Record<string, unknown>;
}

export interface ProposedOperation {
  intent: string;
  targetExternalId: string | null;
  fields: Record<string, unknown>;
  updateMask: string[];
  tagKeys: string[];
  reason: string;
}

export interface CardCaptureView {
  id: string;
  status: 'uploaded' | 'extracting' | 'extracted' | 'extraction_failed'
    | 'reviewed' | 'confirmed' | 'cancelled';
  mimeType: string;
  widthPx: number;
  heightPx: number;
  sizeBytes: number;
  hasImage: boolean;
  correctedFields: string[];
  classification: 'Prospect' | 'Lead' | null;
  policyDecision: 'AUTO' | 'REVIEW' | 'SKIP' | null;
  externalContactId: string | null;
  errorCode: string | null;
  createdAt: string;
}

export interface DraftField {
  value: string | null;
  confidence: number | null;
  verbatim: string | null;
  origin: 'extracted' | 'edited' | 'empty';
}

export interface PostalAddress {
  line1: string | null;
  line2: string | null;
  locality: string | null;
  region: string | null;
  postalCode: string | null;
  countryCode: string | null;
}

/**
 * One number read from a card. `value` is E.164 when it parsed, otherwise as printed.
 * `label` is what was printed beside it ("Cell"), and `typeEvidence` says where the type
 * came from.
 */
export interface DraftPhone {
  value: string;
  valid: boolean;
  type: PhoneType;
  typeEvidence: PhoneTypeEvidence;
  label: string | null;
  confidence: number | null;
  verbatim: string | null;
  /** The reading suggests a fax, but nothing printed says so. Asked beside the number. */
  possibleFax?: boolean;
}

export interface CardDraft {
  givenName: DraftField;
  familyName: DraftField;
  jobTitle: DraftField;
  company: DraftField;
  email: DraftField;
  /** Every number on the card, deduplicated, in the card's order. */
  phones?: DraftPhone[];
  /** The derived primary (first non-fax). Kept by the server for older screens; never edited. */
  phone: DraftField;
  website: DraftField;
  address: PostalAddress | null;
  addressConfidence: number | null;
  imageQuality: 'good' | 'marginal' | 'poor';
  isBusinessCard: boolean;
  warnings: string[];
}

export interface CardReviewResult {
  capture: CardCaptureView;
  identity: Evaluation['identity'];
  policy: Evaluation['policy'];
  proposedOperations: ProposedOperation[];
  requestedTagKeys: string[];
  approvedTagKeys: string[];
  missingTagMappings: string[];
  /** Every number on the card, and what will happen to each before anything is sent. */
  phones?: PhoneDeliveryLine[];
  /** The CRM's contact types, and an existing contact's own. Absent from an older server. */
  contactTypes?: CrmContactTypeOptions;
  existingContactType?: string | null;
  /** The Contact Type this review kept, and where it came from. Absent from an older server. */
  contactType?: string | null;
  contactTypeSource?: 'user' | 'workspace_default' | null;
  /** The card's email is a shared address: whether a person confirmed whose it is, and what Keap holds. */
  sharedAddress?: import('./SharedAddress.js').SharedAddressState | null;
  planHash: string;
  planExpiresAt: string;
  crmWriteEnabled: boolean;
  /** What approving would spend from today's CRM changes. Present when the usage page is on. */
  usage?: ApprovalUsage;
  executed: boolean;
  mutatedCrm: boolean;
}

export type { ApprovalUsage, UsageDimensionView, UsageView } from './usageModel.js';
import type { ApprovalUsage, UsageView } from './usageModel.js';

export interface Evaluation {
  identity: {
    matchTier: string;
    confidence: number;
    requiresReview: boolean;
    externalContactIds: string[];
    alternativeCount: number;
  };
  policy: {
    mode: 'calibration' | 'enforcing';
    decision: 'AUTO' | 'REVIEW' | 'SKIP';
    reason: string;
    reviewReasons: string[];
    skipReason: string | null;
    shadowDecision: string | null;
  };
  proposedOperations: ProposedOperation[];
  approvedTagKeys: string[];
  executed: boolean;
  mutatedCrm: boolean;
}

/* ── The one Review ──────────────────────────────────────────────────
 *
 * GET /review/items: every source in one state-first list. Read-only — each decision is
 * still made on its own source's routes, which re-check every gate.
 */

export type ReviewState =
  'needs_review' | 'ready' | 'waiting' | 'sent' | 'failed' | 'rejected' | 'processing';
export type ReviewCounts = Record<ReviewState | 'all', number>;

/** One selected tag on a row, stage first. Named through GET /crm-tags, never shown raw. */
export interface ReviewRowTag { kind: 'stage' | 'type' | 'tag'; key: string }

export interface EmailReviewRow {
  kind: 'email'; id: string; source: 'email'; state: ReviewState; occurredAt: string;
  status: string; provider: 'gmail' | 'microsoft';
  names: string[]; from: string; company: string | null;
  intent: string | null; commercialStage: string | null; reasons: string[];
  /** Every selected CRM tag. Absent from an older server, which sent only the stage. */
  tags?: ReviewRowTag[];
  /** The conversation's marked messages, oldest first; `id` is the one to open. */
  captureIds?: string[];
  /** Marked messages in this conversation. One row however many there are. */
  messageCount?: number;
  /** People the conversation tried to send, and how many reached the CRM. Absent from an older server. */
  delivery?: { sent: number; tried: number };
  /** The conversation's one stable name, shared by the list and the drawer. Absent from an older server. */
  title?: string;
  /** External people in it; the mailbox owner is never counted. */
  peopleCount?: number;
}
export interface CardReviewRow {
  kind: 'card'; id: string; source: 'business_card'; state: ReviewState; occurredAt: string;
  status: string; name: string | null; company: string | null; commercialStage: string | null;
  tags?: ReviewRowTag[];
  /** `cancelled`, `plan_expired`, `unreadable`, or an execution error code. */
  outcome: string | null;
}
export interface BatchReviewRow {
  kind: 'batch'; id: string; source: 'business_card' | 'import'; state: ReviewState;
  occurredAt: string;
  batchSource: 'csv_import' | 'business_card' | 'contact_list_image';
  label: string | null; total: number;
  counts: Partial<Record<ReviewState, number>>;
}
export type ReviewRow = EmailReviewRow | CardReviewRow | BatchReviewRow;

export interface ReviewItemsResponse {
  view: ReviewView;
  source: 'email' | 'business_card' | 'import' | 'all';
  crmWriteEnabled: boolean;
  /** Both write gates: whether a send could happen now. Absent from an older server. */
  crmWritable?: boolean;
  intentLabels: Record<string, string>;
  /** Per internal state. A batch counts under each state it has contacts in. */
  counts: ReviewCounts;
  /** Per customer view; each batch once per view. These are what the chips show. */
  viewCounts: Record<ReviewView, number>;
  items: ReviewRow[];
}

/** GET /cards/:id — what a card reopened from Review resumes from. */
export interface CardDetailResponse {
  capture: CardCaptureView;
  draft: CardDraft | null;
  reviewed: {
    givenName: string | null; familyName: string | null; jobTitle: string | null;
    company: string | null; email: string | null;
    /** The CRM contact type the person chose, as the CRM names it. */
    contactType?: string | null;
    /** The person's list, when they saved one. A card reviewed before lists has only `phone`. */
    phones?: Array<{ value: string; type: PhoneType }>;
    phone?: string | null;
    website: string | null; address: PostalAddress | null;
  } | null;
  relationshipTypes: string[];
  /** Generic CRM tags the person chose for this card. */
  crmTagKeys?: string[];
  crmWriteEnabled: boolean;
}

/* ── Email → CRM capture ─────────────────────────────────────────────
 *
 * The customer marks an email (a Gmail label or an Outlook category they chose) and only
 * that email is read and analysed. Everything below mirrors the /capture routes; nothing
 * here is provider-specific beyond the marker itself, which genuinely is.
 */

export type CaptureCommercialStage = 'Prospect' | 'Lead' | 'Existing Customer';
export type CaptureMode = 'review' | 'auto';
export type CaptureStatus =
  | 'review_required' | 'auto_pending' | 'approved' | 'executed' | 'partially_executed'
  | 'rejected' | 'failed' | 'held';
export type CaptureIntent =
  | 'buying_inquiry' | 'support_request' | 'billing' | 'scheduling' | 'seller_outreach'
  | 'recruiting' | 'marketing_broadcast' | 'transactional' | 'general' | 'unknown';

/**
 * The email capture settings a customer decides. Workspace-wide default CRM tags for every
 * email were retired: a label's own tags are the only email defaults, and the server ignores
 * the old fields, so they are not part of what is sent.
 */
export interface CaptureSettingsValue {
  mode: CaptureMode;
  includeRecipients: boolean;
  internalDomains: string[];
}

export interface CaptureMarkerView { id: string; name: string; configuredAt: string }

export interface CaptureSettingsResponse {
  settings: CaptureSettingsValue;
  mailbox: null | {
    id: string; provider: 'gmail' | 'microsoft'; address: string; status: string;
    /** The first active marker, for screens that show one. */
    marker: CaptureMarkerView | null;
    /** How many labels/categories are mapped. */
    markers?: number;
  };
  aiLiveEnabled: boolean;
  crmWriteEnabled: boolean;
}

/* ── Capture labels: provider markers mapped to CRM consequences ───────
 *
 * Many Gmail labels / Outlook categories per mailbox. Each one both selects mail for
 * capture and may say how the contact is classified in the CRM — the same canonical
 * vocabulary (commercial stage, relationship types, CRM tags) business-card intake uses.
 */
export interface CaptureMarkerMappingInput {
  commercialStage: CaptureCommercialStage | null;
  relationshipTypeKeys: string[];
  crmTagKeys: string[];
}

export type CaptureMarkerProvider = 'gmail' | 'microsoft';

export interface CaptureMarkerCapability {
  provider: CaptureMarkerProvider;
  canList: boolean;
  canCreate: boolean;
  reconnectFor: 'gmail_labels' | 'outlook_categories' | null;
}

export interface CaptureMarkerMapping extends CaptureMarkerMappingInput {
  id: string;
  emailAccountId: string;
  provider: CaptureMarkerProvider;
  providerMarkerId: string;
  providerMarkerName: string;
  matchKey: string;
  origin: 'existing' | 'created_by_inbox_signal';
  state: 'active' | 'disabled' | 'provider_marker_missing';
  mappingVersion: number;
  configuredAt: string;
}

export interface CaptureMarkerChoice { providerMarkerId: string; name: string; mappedMarkerId: string | null }

export interface CaptureMarkersResponse {
  provider: CaptureMarkerProvider | null;
  kind?: 'label' | 'category';
  capability: CaptureMarkerCapability | null;
  /** null: this grant cannot list them (Outlook without MailboxSettings) — type a name. */
  choices: CaptureMarkerChoice[] | null;
  mappings: CaptureMarkerMapping[];
}

/** @deprecated The single-marker shape of the first capture release. */
export type CaptureMarkerChoices = CaptureMarkersResponse;

/** What a capture inherited from each label applied to the email, frozen at capture time. */
export interface CaptureMarkerConsequence {
  markerId: string;
  providerMarkerName: string;
  mappingVersion: number;
  commercialStage: CaptureCommercialStage | null;
  relationshipTypeKeys: string[];
  crmTagKeys: string[];
}

export type CaptureClassificationSource = 'crm' | 'marker' | 'default' | 'none' | 'human';

export interface CaptureQueueItem {
  id: string;
  status: CaptureStatus;
  mode: CaptureMode;
  provider: 'gmail' | 'microsoft';
  occurredAt: string | null;
  subject: string | null;
  from: string | null;
  intent: CaptureIntent | null;
  intentLabel: string | null;
  summary: string | null;
  company: string | null;
  contacts: { proposed: number; existing: number; new: number; names: string[] };
  commercialStage: CaptureCommercialStage | null;
  relationshipTypeKeys: string[];
  crmTagKeys: string[];
  reviewReasons: string[];
}

export type CaptureFactSource =
  | 'header' | 'signature' | 'body' | 'quoted_signature' | 'crm' | 'provider_identity'
  | 'human_correction';
export interface CaptureFactView { value: string; source: CaptureFactSource }
/** A phone found in an email. `type` only when the signature labelled it. */
export interface CapturePhoneView extends CaptureFactView { type?: PhoneType }

export interface CaptureParticipantView {
  id: string;
  email: string | null;
  displayName: string | null;
  givenName: string | null;
  familyName: string | null;
  phones: CapturePhoneView[];
  title: CaptureFactView | null;
  company: CaptureFactView | null;
  website: CaptureFactView | null;
  role: 'sender' | 'reply_to' | 'to' | 'cc' | 'forwarded_signature';
  isInternal: boolean;
  isRoleMailbox: boolean;
  isAutomated: boolean;
  isFreemail: boolean;
  include: boolean;
  exclusionReason: null | 'internal' | 'role_mailbox' | 'automated_sender' | 'no_address'
    | 'reviewer_removed' | 'participant_limit';
  crm:
    | { kind: 'existing'; externalContactId: string; identityId: string | null; matchedBy: string }
    | { kind: 'new' }
    | { kind: 'ambiguous'; reason: string; candidates?: Array<{ externalContactId: string; name: string | null }> }
    | { kind: 'unresolved'; reason: string };
  enrichment: Array<{
    field: 'phone' | 'jobTitle' | 'company' | 'website';
    proposed: string; current: string | null; conflict: boolean;
  }>;
  /** The CRM contact type a reviewer chose for a NEW contact (the CRM's own identifier). */
  contactType?: string | null;
  /** An EXISTING contact's type as the CRM records it. Shown, never changed. */
  existingContactType?: string | null;
  /** A reviewer's confirmation that this shared address is this person's, on this message. */
  sharedAddressConfirmation?: { email: string; nameKey: string; confirmedAt: string; keapContactId?: string | null } | null;
  /** This person's own CRM Tags; absent means the message's tags apply to them. */
  tags?: { commercialStage: CaptureCommercialStage | null; relationshipTypeKeys: string[]; crmTagKeys: string[] } | null;
  /** Where a new contact's type came from. */
  contactTypeSource?: 'user' | 'source' | 'workspace_default' | null;
  /** A colleague on the connected mailbox's own domain: shown, never pre-selected. */
  sameOrganization?: boolean;
}

/**
 * The connected CRM's own contact types (Keap: the account's Lead / Client / Other),
 * read from the CRM. Separate from CRM tags. `supported: false` means show nothing.
 */
export interface CrmContactTypeOptions {
  supported: boolean;
  required: boolean;
  available: boolean;
  options: Array<{ id: string; label: string }>;
  existingEditable: false;
  /** The workspace's default for a NEW contact (a CRM identifier), or null to ask each time. */
  workspaceDefault?: string | null;
}

/** A marked conversation: each person once, every marked message kept. */
export interface ConversationPersonView extends CaptureParticipantView {
  /** The address every message records; `id` is the person's key (address, or address and name for a shared one). */
  participantId?: string;
  roles: CaptureParticipantView['role'][];
  captureIds: string[];
  openCaptureIds: string[];
  /** Where this person's send stands across the messages that tried. Null when none has. */
  delivery?: { state: 'sent' | 'partly_sent' | 'not_sent'; blockedBy: string | null } | null;
}
export interface ConversationMessageView {
  captureId: string; status: CaptureStatus; mode: CaptureMode; occurredAt: string;
  updatedAt: string; planHash: string; stale: boolean;
  intent: CaptureIntent | null; intentLabel: string | null; summary: string | null;
  reviewReasons: string[]; executedAt: string | null;
  commercialStage: CaptureCommercialStage | null; relationshipTypeKeys: string[]; crmTagKeys: string[];
  classificationSources?: CaptureDetailView['classificationSources'];
  subject: string | null; from: string | null; fromDisplayName: string | null;
  to: string[]; cc: string[]; webLink: string | null; redacted: boolean; markers: string[];
  participantIds: string[];
  execution: CaptureDetailView['execution'];
  /** Why an approved message has not finished: a person must change it, a write gate holds it, or the CRM did not complete it. */
  stoppedBy?: 'person' | 'hold' | 'error' | null;
}
export interface ConversationResponse {
  anchorId: string;
  provider: 'gmail' | 'microsoft';
  messages: ConversationMessageView[];
  people: ConversationPersonView[];
  contactTypes: CrmContactTypeOptions;
  phoneDelivery?: Record<string, PhoneDeliveryLine[]>;
  /** Each existing contact's Contact Type as Keap holds it now, by Keap contact id. */
  keapContactTypes?: Record<string, KeapContactTypeRead>;
  crmWriteEnabled: boolean;
}
/** An existing contact's Contact Type, read from Keap: known (null = not set), or could not be read. */
export interface KeapContactTypeRead { status: 'ok' | 'unavailable'; contactType: string | null }
export interface ConversationEdit {
  versions: Record<string, string>;
  people: CaptureEdit['participants'];
  messages: Array<{ captureId: string; intent?: CaptureIntent; summary?: string | null }>;
  commercialStage: CaptureCommercialStage | null;
  relationshipTypeKeys: string[];
  crmTagKeys: string[];
}

export interface CaptureDetailView {
  id: string;
  status: CaptureStatus;
  mode: CaptureMode;
  provider: 'gmail' | 'microsoft';
  /** The ORIGINAL email's time, not when it was marked or processed. */
  occurredAt: string | null;
  reviewReasons: string[];
  intent: CaptureIntent | null;
  intentLabel: string | null;
  intentSource: string | null;
  summary: string | null;
  summarySource: string | null;
  participants: CaptureParticipantView[];
  company: null | {
    name: string | null; domain: string | null; website: string | null;
    evidence: Array<{ kind: 'signature' | 'body' | 'shared_domain' | 'website' | 'crm' | 'human'; value: string }>;
    participantIds: string[];
  };
  commercialStage: CaptureCommercialStage | null;
  relationshipTypeKeys: string[];
  crmTagKeys: string[];
  planHash: string;
  /** The version a correction is bound to; sent back as `expectedUpdatedAt`. */
  updatedAt: string;
  execution: Record<string, {
    state: string; externalContactId: string | null; created: boolean;
    noteWritten: boolean; tagsApplied: boolean; errorCode: string | null;
  }>;
  stale: boolean;
  /** Why an approved capture has not finished: a person must change it, a write gate holds it, or the CRM did not complete it. */
  stoppedBy?: 'person' | 'hold' | 'error' | null;
  decidedAt: string | null;
  executedAt: string | null;
  /** Why this email was captured: every mapped label it carried. */
  markerConsequences?: CaptureMarkerConsequence[];
  /** Where each classification field's value came from. */
  classificationSources?: {
    commercialStage: CaptureClassificationSource;
    relationshipTypes: CaptureClassificationSource;
    crmTags: CaptureClassificationSource;
  };
}

export interface CaptureDetailResponse {
  capture: CaptureDetailView;
  /** What will happen to each participant's numbers, by participant id. */
  phoneDelivery?: Record<string, PhoneDeliveryLine[]>;
  /** Each existing contact's Contact Type as Keap holds it now, by Keap contact id. */
  keapContactTypes?: Record<string, KeapContactTypeRead>;
  source: {
    provider: 'gmail' | 'microsoft';
    occurredAt: string | null;
    subject: string | null;
    from: string | null;
    fromDisplayName: string | null;
    to: string[];
    cc: string[];
    authored: string | null;
    quoted: string | null;
    webLink: string | null;
    /** The label(s) or category the customer applied, as context — never a setting. */
    markers?: string[];
    redacted: boolean;
  };
  /** The CRM's contact types. Absent from an older server. */
  contactTypes?: CrmContactTypeOptions;
  crmWriteEnabled: boolean;
}

export interface CaptureEdit {
  expectedUpdatedAt: string;
  participants: Array<{
    id: string; include: boolean; displayName?: string | null;
    /** Every number kept, in order, each with its type. Replaces the participant's list. */
    phones?: Array<{ value: string; type: PhoneType }>;
    title?: string | null; company?: string | null; website?: string | null;
    /** A NEW contact's CRM contact type; null clears it. */
    contactType?: string | null;
    /** A shared address: true confirms it is this person's; false is "Skip this person". */
    confirmSharedAddress?: boolean;
    /** For a confirmed shared address Keap already holds: the holder chosen as this person. */
    keapContactId?: string;
    /** Where the Contact Type came from: chosen, or the workspace default pre-filled. */
    contactTypeSource?: 'user' | 'workspace_default';
    /** This person's own CRM Tags; null returns them to the message's. */
    tags?: { commercialStage: CaptureCommercialStage | null; relationshipTypeKeys: string[]; crmTagKeys: string[] } | null;
  }>;
  intent?: CaptureIntent;
  summary?: string | null;
  commercialStage: CaptureCommercialStage | null;
  relationshipTypeKeys: string[];
  crmTagKeys: string[];
  company?: { name?: string | null; website?: string | null };
}

export interface CaptureExecutionResult {
  executed: boolean;
  status: CaptureStatus | null;
  refusal?: string;
  contacts: Record<string, unknown>;
  crmWriteEnabled: boolean;
}

export interface CaptureMetrics {
  triggersObserved: number; contentRetrieved: number; captures: number; aiCalls: number;
  fastTierCalls: number; deepTierCalls: number; estimatedCostMicros: number;
  contactsProposed: number; contactsMatchedExisting: number; contactsCreated: number;
  reviewCaptures: number; autoCaptures: number; unmarkedMailboxAiCalls: number;
  sinceDays: number;
}

export const api = createApi({
  reducerPath: 'api',
  baseQuery: fetchBaseQuery({
    baseUrl: '/api',
    // The session cookie is HttpOnly, so it is never readable from JavaScript.
    credentials: 'same-origin',
    prepareHeaders: (headers) => {
      headers.set('x-csrf-token', csrfToken());
      return headers;
    },
  }),
  tagTypes: [
    'Me', 'Workspace', 'Connections', 'TagMappings', 'Overview', 'Activity', 'Review', 'Destinations',
    'RelationshipTypes', 'Capture', 'CaptureMarkers', 'Usage',
  ],
  endpoints: (b) => ({
    me: b.query<Me, void>({ query: () => '/me', providesTags: ['Me'] }),
    workspace: b.query<Workspace, void>({ query: () => '/workspace', providesTags: ['Workspace'] }),
    workspaces: b.query<{ workspaces: WorkspaceSummary[] }, void>({
      query: () => '/workspaces', providesTags: ['Workspace'],
    }),
    login: b.mutation<Me, { email: string; password: string }>({
      query: (body) => ({ url: '/auth/login', method: 'POST', body }),
      invalidatesTags: ['Me', 'Workspace', 'Connections', 'TagMappings', 'Overview', 'Activity'],
    }),
    logout: b.mutation<void, void>({
      query: () => ({ url: '/auth/logout', method: 'POST' }),
      invalidatesTags: ['Me', 'Workspace', 'Connections', 'TagMappings', 'Overview', 'Activity'],
    }),
    switchWorkspace: b.mutation<{ workspaceId: string; role: string }, { workspaceId: string }>({
      query: (body) => ({ url: '/session/workspace', method: 'POST', body }),
      invalidatesTags: ['Me', 'Workspace', 'Connections', 'TagMappings', 'Overview', 'Activity'],
    }),

    connections: b.query<{ connectors: Connector[] }, void>({
      query: () => '/connections', providesTags: ['Connections'],
    }),
    /**
     * The key travels in the request body over the same-origin session, and the
     * response carries only the connector's public state. Nothing here holds it.
     */
    connectKeap: b.mutation<{ connector: Connector }, { serviceAccountKey: string }>({
      query: (body) => ({ url: '/connections/keap', method: 'POST', body }),
      invalidatesTags: ['Connections', 'Activity'],
    }),
    verifyKeap: b.mutation<{ connector: Connector; tagCount: number; readOnly: boolean }, void>({
      query: () => ({ url: '/connections/keap/verify', method: 'POST' }),
      invalidatesTags: ['Connections', 'Activity'],
    }),
    keapTags: b.query<{ tags: KeapTag[]; readOnly: boolean }, void>({
      query: () => '/connections/keap/tags',
    }),
    /**
     * The one list of CRM tags a customer chooses from, whatever the capture source.
     * Each entry says which internal field it travels in; crmTagSelection.ts translates.
     */
    crmTags: b.query<CrmTagVocabulary, void>({
      query: () => '/crm-tags', providesTags: ['TagMappings', 'RelationshipTypes'],
    }),

    /**
     * Begins the Gmail consent flow.
     *
     * Returns where to send the browser rather than redirecting, so the page can show
     * its own "taking you to Google" state instead of the address bar changing under
     * the user with no explanation.
     */
    startGmail: b.mutation<
      { authorizationUrl: string },
      { returnPath?: string; labelManagement?: boolean } | void
    >({
      query: (body) => ({ url: '/connections/gmail/start', method: 'POST', body: body ?? {} }),
    }),
    /**
     * Deletes a historical mailbox's stored data.
     *
     * Provider-agnostic on purpose: it acts on a mailbox that is no longer anybody's
     * connection, so which provider once fed it is not the interesting fact about it.
     */
    deleteHistoricalMailbox: b.mutation<
      { deleted: boolean; messagesRemoved: number; reviewTasksRemoved: number },
      { emailAccountId: string }
    >({
      query: ({ emailAccountId }) => ({
        url: `/mailboxes/${emailAccountId}/delete`, method: 'POST', body: {},
      }),
      invalidatesTags: ['Connections', 'Activity', 'Review'],
    }),
    /* ── Workspace-defined relationship types ─────────────────────────── */

    relationshipTypes: b.query<{
      commercialStages: string[];
      relationshipTypes: {
        canonicalKey: string; displayName: string; enabled: boolean; crmMapped: boolean;
      }[];
    }, void>({
      query: () => '/relationship-types',
      providesTags: ['RelationshipTypes'],
    }),
    createRelationshipType: b.mutation<
      { created: boolean; canonicalKey: string },
      { displayName: string; externalTagId?: string | null }
    >({
      query: (body) => ({ url: '/relationship-types', method: 'POST', body }),
      invalidatesTags: ['RelationshipTypes', 'TagMappings', 'Capture'],
    }),
    updateRelationshipType: b.mutation<
      { updated: boolean; canonicalKey: string },
      { key: string; displayName?: string; enabled?: boolean; externalTagId?: string | null }
    >({
      query: ({ key, ...body }) => ({
        url: `/relationship-types/${encodeURIComponent(key)}`, method: 'PATCH', body,
      }),
      invalidatesTags: ['RelationshipTypes', 'TagMappings', 'Capture'],
    }),
    disconnectGmail: b.mutation<
      { disconnected: boolean; revokedAtGoogle: boolean; deletedNow: boolean },
      { deleteData: boolean }
    >({
      query: (body) => ({ url: '/connections/gmail/disconnect', method: 'POST', body }),
      invalidatesTags: ['Connections', 'Activity'],
    }),

    /**
     * Begins the Outlook consent flow. Same shape as Gmail's, and deliberately so — the
     * only asymmetry a customer should meet between the two connectors is the name on
     * the sign-in page.
     */
    startMicrosoft: b.mutation<
      { authorizationUrl: string }, { returnPath?: string; categoryManagement?: boolean } | void
    >({
      query: (body) => ({ url: '/connections/microsoft/start', method: 'POST', body: body ?? {} }),
    }),
    /**
     * Disconnects Outlook.
     *
     * The response has no `revoked` boolean to read, because Microsoft publishes no
     * per-application revocation endpoint for the server to call. What comes back is
     * what actually happened locally, plus where the user finishes the job.
     */
    disconnectMicrosoft: b.mutation<{
      disconnected: boolean;
      localCredentialPurged: boolean;
      providerRevocationIsUserInitiated: boolean;
      revokeAtProviderUrl: string;
      deletedNow: boolean;
    }, { deleteData: boolean }>({
      query: (body) => ({ url: '/connections/microsoft/disconnect', method: 'POST', body }),
      invalidatesTags: ['Connections', 'Activity'],
    }),
    reviewQueue: b.query<
      { items: ReviewItem[]; nextBefore: string | null; crmWriteEnabled: boolean },
      { status?: string; filter?: string; limit?: number; before?: string } | void
    >({
      query: (a) => {
        const q = new URLSearchParams();
        if (a?.status) q.set('status', a.status);
        if (a?.filter && a.filter !== 'all') q.set('filter', a.filter);
        if (a?.limit) q.set('limit', String(a.limit));
        if (a?.before) q.set('before', a.before);
        const s = q.toString();
        return `/review/email${s ? `?${s}` : ''}`;
      },
      providesTags: ['Review'],
    }),

    /**
     * One message, as a person needs to see it to judge a classification.
     *
     * Keyed on the MESSAGE, not on a review task, because most classified mail never
     * produces one — and "why did it label this Newsletter" is worth answering about the
     * cases it got right, which is where trust in an automatic labeller is actually built.
     */
    messageView: b.query<MessageView, string>({
      query: (id) => `/messages/${id}/view`,
      providesTags: ['Review'],
    }),

    /**
     * Corrects the Inbox Labels on a reading.
     *
     * The complete intended set, not a delta: the server has to know what was REMOVED to
     * record that the classifier was wrong about it. Touches no CRM tag and calls no
     * provider.
     */
    saveLabelFeedback: b.mutation<
      { labelKeys: string[]; recorded: number }, { id: string; labelKeys: string[] }
    >({
      query: ({ id, labelKeys }) => ({
        url: `/review/email/${id}/labels`, method: 'PUT', body: { labelKeys },
      }),
      invalidatesTags: ['Review'],
    }),

    /**
     * One item, with the context the queue row deliberately leaves out.
     *
     * The list is the scanning surface and the detail is the deciding surface, so the
     * message excerpt is fetched only when somebody actually opens something rather than
     * shipped for every row nobody looked at.
     */
    reviewItem: b.query<
      {
        item: ReviewItem; excerpt: string | null; excerptTruncated: boolean;
        stale: { reason: string; detail: string } | null; crmWriteEnabled: boolean;
      },
      string
    >({
      query: (id) => `/review/email/${id}`,
      providesTags: ['Review'],
    }),
    classifiedMessages: b.query<
      { items: ClassifiedMessage[]; nextBefore: string | null }, { limit?: number } | void
    >({
      query: (a) => `/messages/classified${a?.limit ? `?limit=${a.limit}` : ''}`,
      providesTags: ['Review'],
    }),
    approveReview: b.mutation<
      { approved: boolean; executed: boolean; crmWriteEnabled: boolean; state: string }, { id: string }
    >({
      query: ({ id }) => ({ url: `/review/email/${id}/approve`, method: 'POST' }),
      invalidatesTags: ['Review', 'Activity'],
    }),
    rejectReview: b.mutation<{ rejected: boolean }, { id: string }>({
      query: ({ id }) => ({ url: `/review/email/${id}/reject`, method: 'POST' }),
      invalidatesTags: ['Review', 'Activity'],
    }),
    noActionReview: b.mutation<{ closed: boolean }, { id: string }>({
      query: ({ id }) => ({ url: `/review/email/${id}/no-action`, method: 'POST' }),
      invalidatesTags: ['Review', 'Activity'],
    }),
    editReviewTags: b.mutation<{ tagKeys: string[] }, { id: string; tagKeys: string[] }>({
      query: ({ id, tagKeys }) => ({ url: `/review/email/${id}/tags`, method: 'PUT', body: { tagKeys } }),
      invalidatesTags: ['Review'],
    }),
    correctRelationship: b.mutation<
      { relationship: string }, { id: string; relationship: string }
    >({
      query: ({ id, relationship }) => ({
        url: `/review/email/${id}/relationship`, method: 'PUT', body: { relationship },
      }),
      invalidatesTags: ['Review'],
    }),

    /** A bounded read. The caller chooses how small, and cannot choose large. */
    syncGmail: b.mutation<
      { report: SyncReport; readOnly: boolean },
      { mode: 'initial' | 'incremental'; maxMessages?: number }
    >({
      query: ({ mode, ...body }) => ({
        url: `/connections/gmail/sync/${mode}`, method: 'POST', body,
      }),
      invalidatesTags: ['Connections', 'Activity', 'Overview'],
    }),

    /**
     * The same bounded read against Outlook.
     *
     * `mode` is one of two literal strings the client interpolates into a fixed path.
     * It is not a Graph URL, a folder, or a filter — there is no request this client can
     * make that means "fetch this Microsoft address for me".
     */
    syncMicrosoft: b.mutation<
      { report: SyncReport; readOnly: boolean },
      { mode: 'initial' | 'incremental'; maxMessages?: number }
    >({
      query: ({ mode, ...body }) => ({
        url: `/connections/microsoft/sync/${mode}`, method: 'POST', body,
      }),
      invalidatesTags: ['Connections', 'Activity', 'Overview'],
    }),

    tagMappings: b.query<
      { mappings: TagMappingRow[]; approvedTags: ApprovedTagRow[]; categories: CategoryRule[] },
      void
    >({
      query: () => '/tag-mappings', providesTags: ['TagMappings'],
    }),
    approveTag: b.mutation<{ approved: { canonicalKey: string } }, {
      canonicalKey: string; externalTagId: string; displayName: string;
      category: TagCategory; aiAllowed: boolean; autoApplyAllowed: boolean;
      reviewRequired: boolean; aiGuidance?: string | null;
    }>({
      query: (body) => ({ url: '/ai-vocabulary', method: 'PUT', body }),
      invalidatesTags: ['TagMappings', 'Activity', 'Capture'],
    }),
    withdrawTag: b.mutation<void, string>({
      query: (key) => ({ url: `/ai-vocabulary/${encodeURIComponent(key)}`, method: 'DELETE' }),
      invalidatesTags: ['TagMappings', 'Activity', 'Capture'],
    }),
    saveTagMapping: b.mutation<
      { mapping: { canonicalKey: string; externalTagId: string }; mutatedCrm: boolean },
      { canonicalKey: string; externalTagId: string; displayName?: string | null }
    >({
      query: (body) => ({ url: '/tag-mappings', method: 'PUT', body }),
      invalidatesTags: ['TagMappings', 'Overview', 'Activity', 'Capture'],
    }),

    tagAssociations: b.query<{
      relationships: RelationshipAssociationRow[];
      choosableTags: ChoosableTag[];
      associableCategories: TagCategory[];
    }, void>({
      query: () => '/tag-associations', providesTags: ['TagMappings'],
    }),
    saveTagAssociation: b.mutation<
      { relationshipKey: string; additionalTagKeys: string[] },
      { relationshipKey: string; approvedTagKeys: string[] }
    >({
      query: (body) => ({ url: '/tag-associations', method: 'PUT', body }),
      invalidatesTags: ['TagMappings', 'Activity'],
    }),

    overview: b.query<Overview, void>({ query: () => '/overview', providesTags: ['Overview'] }),
    /** 404 while the usage page is switched off; the settings entry is hidden then. */
    usage: b.query<UsageView, void>({ query: () => '/usage', providesTags: ['Usage'] }),
    companionConnections: b.query<{ connections: CompanionConnection[]; limits: { dailyCaptures: number } }, void>({
      query: () => '/companion/connections', providesTags: ['Destinations'],
    }),
    revokeCompanion: b.mutation<{ revoked: boolean }, string>({
      query: (id) => ({ url: `/companion/connections/${id}/revoke`, method: 'POST' }), invalidatesTags: ['Destinations'],
    }),
    authorizeCompanion: b.mutation<{ code: string }, { redirectUri: string; codeChallenge: string }>({
      query: (body) => ({ url: '/companion/authorize', method: 'POST', body }),
    }),
    destinations: b.query<DestinationsResponse, void>({ query: () => '/destinations', providesTags: ['Destinations'] }),
    createWebhook: b.mutation<{ destination: DestinationView; signingSecret: string }, { name: string; url: string; organization: DestinationOrganizationView }>({
      query: (body) => ({ url: '/destinations/webhooks', method: 'POST', body }), invalidatesTags: ['Destinations', 'Activity'],
    }),
    updateDestination: b.mutation<{ destination: DestinationView | null }, { id: string; status?: 'active' | 'disabled'; organization?: DestinationOrganizationView }>({
      query: ({ id, ...body }) => ({ url: `/destinations/${id}`, method: 'PATCH', body }), invalidatesTags: ['Destinations'],
    }),
    revokeDestination: b.mutation<{ revoked: boolean }, string>({
      query: (id) => ({ url: `/destinations/${id}/revoke`, method: 'POST' }), invalidatesTags: ['Destinations'],
    }),
    rotateDestinationSecret: b.mutation<{ signingSecret: string }, string>({
      query: (id) => ({ url: `/destinations/${id}/rotate-secret`, method: 'POST' }), invalidatesTags: ['Destinations'],
    }),
    testDestination: b.mutation<{ ok: boolean; statusCode: number | null; errorCode: string | null }, string>({
      query: (id) => ({ url: `/destinations/${id}/test`, method: 'POST' }), invalidatesTags: ['Destinations'],
    }),
    retryDelivery: b.mutation<{ queued: boolean }, string>({
      query: (id) => ({ url: `/destinations/deliveries/${id}/retry`, method: 'POST' }), invalidatesTags: ['Destinations'],
    }),
    createZapierKey: b.mutation<{ key: ZapierKeyView; secret: string }, { name: string }>({
      query: (body) => ({ url: '/zapier-keys', method: 'POST', body }), invalidatesTags: ['Destinations'],
    }),
    revokeZapierKey: b.mutation<{ revoked: boolean }, string>({
      query: (id) => ({ url: `/zapier-keys/${id}/revoke`, method: 'POST' }), invalidatesTags: ['Destinations'],
    }),
    /** `customer`: only captures, CRM results and finished imports — the Dashboard's feed. */
    activity: b.query<{ events: ActivityEvent[] }, { scope?: 'customer' } | void>({
      query: (arg) => (arg && arg.scope ? `/activity?scope=${arg.scope}` : '/activity'),
      providesTags: ['Activity'],
    }),

    /**
     * The raw file as the body. There is no multipart envelope and no filename: the
     * server derives the storage key itself, so nothing about the local file can
     * influence where the bytes land.
     */
    uploadCard: b.mutation<
      { capture: CardCaptureView; intakeItemId?: string; duplicate?: boolean },
      File | { file: File; batchId: string }
    >({
      query: (arg) => {
        const file = arg instanceof File ? arg : arg.file;
        return {
          url: '/cards',
          method: 'POST',
          body: file,
          headers: {
            'content-type': file.type,
            // Present only for a batch. The single-card camera flow sends no header and
            // reaches exactly the path it always did.
            ...(arg instanceof File ? {} : { 'x-intake-batch': arg.batchId }),
          },
        };
      },
      invalidatesTags: ['Activity'],
    }),

    intakeItem: b.query<IntakeItemDetail, string>({
      query: (id) => `/intake/items/${id}`, providesTags: ['Activity'],
    }),
    editIntakeItem: b.mutation<unknown, { id: string; patch: Record<string, unknown> }>({
      query: ({ id, patch }) => ({ url: `/intake/items/${id}`, method: 'PUT', body: patch }),
      invalidatesTags: ['Activity', 'Review'],
    }),
    recheckIntakeItem: b.mutation<unknown, string>({
      query: (id) => ({ url: `/intake/items/${id}/recheck`, method: 'POST', body: {} }),
      invalidatesTags: ['Activity', 'Review'],
    }),
    decideIntakeItem: b.mutation<
      { itemId: string; state: string; executed: boolean; alreadyDecided?: boolean; destinationOnly?: boolean },
      { id: string; state: 'approved' | 'rejected'; planHash?: string | null; reason?: string }
    >({
      query: ({ id, ...body }) => ({ url: `/intake/items/${id}/decision`, method: 'POST', body }),
      // The badge is a count of open decisions, so making one has to move it.
      invalidatesTags: ['Activity', 'Review'],
    }),
    bulkIntakeAction: b.mutation<
      { results: Array<{ itemId: string; outcome: string }> } & Record<string, unknown>,
      { itemIds: string[]; action: string; relationship?: string | null; tagKey?: string; contactType?: string; planHashes?: Record<string, string> }
    >({
      query: (body) => ({ url: '/intake/items/bulk', method: 'POST', body }),
      invalidatesTags: ['Activity', 'Review'],
    }),

    intakeSettings: b.query<IntakeSettings, void>({
      query: () => '/intake/settings', providesTags: ['TagMappings'],
    }),
    saveIntakeSettings: b.mutation<
      { defaultPhoneRegion: string | null },
      { defaultPhoneRegion: string | null; emailLabelingEnabled?: boolean }
    >({
      query: (body) => ({ url: '/intake/settings', method: 'PUT', body }),
      invalidatesTags: ['TagMappings', 'Activity'],
    }),
    /** The one default CRM tag for image and file capture: a stage, one other tag, or none. */
    saveIntakeDefaultTags: b.mutation<
      unknown,
      { defaultCommercialStage: CaptureCommercialStage | null; defaultCrmTagKeys: string[] }
    >({
      query: (body) => ({ url: '/intake/default-tags', method: 'PUT', body }),
      invalidatesTags: ['TagMappings', 'Activity'],
    }),

    uploadCsv: b.mutation<CsvUploadResult, File>({
      query: (file) => ({
        url: '/intake/csv', method: 'POST', body: file,
        headers: { 'content-type': 'text/csv' },
      }),
      invalidatesTags: ['Activity'],
    }),
    confirmCsvMapping: b.mutation<
      {
        batch: IntakeBatch;
        tally: { total: number; byStatus: Record<string, number> };
        progress: IntakeProgress;
        crmConnected: boolean;
        /** True when a run was actually started. False means one was already going. */
        processing: boolean;
      },
      { file: File; batchId: string; mapping: Record<string, string> }
    >({
      query: ({ file, batchId, mapping }) => ({
        url: '/intake/csv/confirm', method: 'POST', body: file,
        headers: {
          'content-type': 'text/csv',
          'x-intake-batch': batchId,
          'x-intake-mapping': JSON.stringify(mapping),
        },
      }),
      invalidatesTags: ['Activity'],
    }),

    openIntakeBatch: b.mutation<
      { batch: IntakeBatch; alreadyExists: boolean },
      { source: string; label?: string; sourceFingerprint?: string }
    >({
      query: (body) => ({ url: '/intake/batches', method: 'POST', body }),
    }),
    intakeBatches: b.query<{
      batches: Array<IntakeBatch & {
        tally: { total: number; byStatus: Record<string, number> };
        progress: IntakeProgress;
      }>;
      limits: Record<string, number>;
    }, void>({
      query: () => '/intake/batches', providesTags: ['Activity'],
    }),
    intakeBatch: b.query<{
      batch: IntakeBatch;
      tally: { total: number; byStatus: Record<string, number> };
      progress: IntakeProgress;
      /** Whether picking the import up can check its waiting rows against a CRM. */
      crmConnected: boolean;
      items: IntakeItem[];
    }, string>({
      query: (id) => `/intake/batches/${id}`,
    }),
    cancelIntakeBatch: b.mutation<
      { batchId: string; requested: boolean; cancelled: number; progress: IntakeProgress },
      string
    >({
      query: (id) => ({ url: `/intake/batches/${id}/cancel`, method: 'POST' }),
      invalidatesTags: ['Activity', 'Review'],
    }),
    resumeIntakeBatch: b.mutation<
      { batchId: string; requeued: number; started: boolean; progress: IntakeProgress },
      string
    >({
      query: (id) => ({ url: `/intake/batches/${id}/resume`, method: 'POST' }),
      invalidatesTags: ['Activity', 'Review'],
    }),
    /*
     * One number for the badge, counted from one table.
     *
     * Adding an intake count to an email count would double every intake decision that
     * already has a review task — which is why the server counts tasks and this asks for
     * that count rather than assembling its own.
     */
    reviewCount: b.query<
      { total: number; email: number; intake: number; capture?: number; attention?: number }, void
    >({
      query: () => '/review/count', providesTags: ['Review', 'Capture', 'Activity'],
    }),
    /** The one Review list, state first and source as a filter. */
    reviewItems: b.query<ReviewItemsResponse, { view: ReviewView; source: string }>({
      query: ({ view, source }) => `/review/items?${new URLSearchParams({ view, source }).toString()}`,
      providesTags: ['Review', 'Capture', 'Activity'],
    }),
    card: b.query<CardDetailResponse, string>({
      query: (id) => `/cards/${encodeURIComponent(id)}`, providesTags: ['Review'],
    }),
    /* ── Email → CRM capture ───────────────────────────────────────── */

    captureSettings: b.query<CaptureSettingsResponse, void>({
      query: () => '/capture/settings', providesTags: ['Capture'],
    }),
    /** The whole settings object, every field: the server replaces rather than merges. */
    saveCaptureSettings: b.mutation<{ settings: CaptureSettingsValue }, CaptureSettingsValue>({
      query: (body) => ({ url: '/capture/settings', method: 'PUT', body }),
      invalidatesTags: ['Capture', 'Activity'],
    }),
    captureMarkers: b.query<CaptureMarkersResponse, void>({
      query: () => '/capture/markers', providesTags: ['CaptureMarkers'],
    }),
    /** @deprecated Single-marker compatibility route; the mapping manager replaced it. */
    saveCaptureMarker: b.mutation<
      { marker: CaptureMarkerView | null },
      { labelId: string } | { categoryName: string } | { clear: true }
    >({
      query: (body) => ({ url: '/capture/marker', method: 'PUT', body }),
      invalidatesTags: ['Capture', 'CaptureMarkers', 'Activity'],
    }),
    mapCaptureMarker: b.mutation<
      { marker: CaptureMarkerMapping },
      ({ providerMarkerId: string } | { categoryName: string }) & { mapping: CaptureMarkerMappingInput }
    >({
      query: (body) => ({ url: '/capture/markers', method: 'POST', body }),
      invalidatesTags: ['Capture', 'CaptureMarkers', 'Activity'],
    }),
    createCaptureMarker: b.mutation<
      { marker: CaptureMarkerMapping; providerCreated: boolean },
      { name: string; mapping: CaptureMarkerMappingInput }
    >({
      query: (body) => ({ url: '/capture/markers/create', method: 'POST', body }),
      invalidatesTags: ['Capture', 'CaptureMarkers', 'Activity'],
    }),
    updateCaptureMarker: b.mutation<
      { marker: CaptureMarkerMapping }, { id: string; mapping: CaptureMarkerMappingInput }
    >({
      query: ({ id, mapping }) => ({
        url: `/capture/markers/${encodeURIComponent(id)}`, method: 'PUT', body: { mapping },
      }),
      invalidatesTags: ['Capture', 'CaptureMarkers', 'Activity'],
    }),
    setCaptureMarkerEnabled: b.mutation<
      { marker: CaptureMarkerMapping }, { id: string; enabled: boolean }
    >({
      query: ({ id, enabled }) => ({
        url: `/capture/markers/${encodeURIComponent(id)}/state`, method: 'PUT', body: { enabled },
      }),
      invalidatesTags: ['Capture', 'CaptureMarkers', 'Activity'],
    }),
    removeCaptureMarker: b.mutation<
      { providerDeleted: boolean }, { id: string; deleteProviderMarker: boolean }
    >({
      query: ({ id, deleteProviderMarker }) => ({
        url: `/capture/markers/${encodeURIComponent(id)}/remove`, method: 'POST',
        body: { deleteProviderMarker },
      }),
      invalidatesTags: ['Capture', 'CaptureMarkers', 'Activity'],
    }),
    captureQueue: b.query<
      { items: CaptureQueueItem[]; nextBefore: string | null },
      { filter: 'open' | 'executed' | 'rejected' | 'all'; before?: string }
    >({
      query: ({ filter, before }) => {
        const q = new URLSearchParams({ filter });
        if (before) q.set('before', before);
        return `/capture/queue?${q.toString()}`;
      },
      providesTags: ['Capture'],
    }),
    captureMetrics: b.query<CaptureMetrics, void>({
      query: () => '/capture/metrics', providesTags: ['Capture'],
    }),
    capture: b.query<CaptureDetailResponse, string>({
      query: (id) => `/capture/${encodeURIComponent(id)}`, providesTags: ['Capture'],
    }),
    editCapture: b.mutation<{ capture: CaptureDetailView }, { id: string; body: CaptureEdit }>({
      query: ({ id, body }) => ({ url: `/capture/${encodeURIComponent(id)}`, method: 'PUT', body }),
      // Only a save that went through refreshes the capture: a refused one keeps what was typed.
      invalidatesTags: (result) => (result ? ['Capture'] : []),
    }),
    approveCapture: b.mutation<CaptureExecutionResult, { id: string; planHash: string }>({
      query: ({ id, planHash }) => ({
        url: `/capture/${encodeURIComponent(id)}/approve`, method: 'POST', body: { planHash },
      }),
      // The badge counts open captures, so deciding one has to move it.
      invalidatesTags: ['Capture', 'Review', 'Activity'],
    }),
    retryCapture: b.mutation<CaptureExecutionResult, { id: string }>({
      query: ({ id }) => ({ url: `/capture/${encodeURIComponent(id)}/retry`, method: 'POST', body: {} }),
      invalidatesTags: ['Capture', 'Review', 'Activity'],
    }),
    /** "Review and fix": back to Review. Sends nothing and retries nothing. */
    reopenCapture: b.mutation<{ reopened: number; mutatedCrm: false }, { id: string }>({
      query: ({ id }) => ({ url: `/capture/${encodeURIComponent(id)}/reopen`, method: 'POST', body: {} }),
      invalidatesTags: ['Capture', 'Review'],
    }),
    crmContactTypes: b.query<CrmContactTypeOptions, void>({
      query: () => '/crm/contact-types', providesTags: ['Capture'],
    }),
    /** An existing contact's Contact Type, changed by a person's approval. One field, one contact. */
    updateContactType: b.mutation<
      { status: 'updated' | 'checking'; contactType?: string; replayed?: boolean },
      { externalContactId: string; expected: string | null; contactType: string; requestId: string;
        source: { kind: 'email'; captureId: string; personId: string } | { kind: 'card'; captureId: string } | { kind: 'intake'; itemId: string } }
    >({
      query: (body) => ({ url: '/crm/contact-type-updates', method: 'POST', body }),
      invalidatesTags: ['Capture', 'Review', 'Activity'],
    }),
    /** Settings → CRM → Contact defaults: the Contact Type a new contact starts with in Review. */
    contactDefaults: b.query<{ defaultContactType: string; configured: boolean; recommended: string; contactTypes: CrmContactTypeOptions }, void>({
      query: () => '/crm/contact-defaults', providesTags: ['Capture'],
    }),
    saveContactDefaults: b.mutation<{ defaultContactType: string; configured: boolean }, { defaultContactType: string }>({
      query: (body) => ({ url: '/crm/contact-defaults', method: 'PUT', body }),
      invalidatesTags: ['Capture'],
    }),
    conversation: b.query<ConversationResponse, string>({
      query: (id) => `/capture/${encodeURIComponent(id)}/conversation`, providesTags: ['Capture'],
    }),
    editConversation: b.mutation<{ saved: Array<{ captureId: string; updatedAt: string; planHash: string }> }, { id: string; body: ConversationEdit }>({
      query: ({ id, body }) => ({ url: `/capture/${encodeURIComponent(id)}/conversation`, method: 'PUT', body }),
      invalidatesTags: (result) => (result ? ['Capture'] : []),
    }),
    approveConversation: b.mutation<
      { executed: boolean; results: Array<{ captureId: string; executed: boolean; status: string | null; refusal?: string }>; crmWriteEnabled: boolean },
      { id: string; planHashes: Record<string, string> }
    >({
      query: ({ id, planHashes }) => ({
        url: `/capture/${encodeURIComponent(id)}/conversation/approve`, method: 'POST', body: { planHashes },
      }),
      invalidatesTags: ['Capture', 'Review', 'Activity'],
    }),
    retryConversation: b.mutation<
      { executed: boolean; results: Array<{ captureId: string; executed: boolean; status: string | null; refusal?: string }>; crmWriteEnabled: boolean },
      { id: string }
    >({
      query: ({ id }) => ({ url: `/capture/${encodeURIComponent(id)}/conversation/retry`, method: 'POST', body: {} }),
      invalidatesTags: ['Capture', 'Review', 'Activity'],
    }),
    /** "Review and fix" for a conversation: its stopped messages back to Review. Sends nothing. */
    reopenConversation: b.mutation<{ reopened: number; mutatedCrm: false }, { id: string }>({
      query: ({ id }) => ({ url: `/capture/${encodeURIComponent(id)}/conversation/reopen`, method: 'POST', body: {} }),
      invalidatesTags: ['Capture', 'Review'],
    }),
    rejectConversation: b.mutation<{ rejected: number }, { id: string }>({
      query: ({ id }) => ({ url: `/capture/${encodeURIComponent(id)}/conversation/reject`, method: 'POST', body: {} }),
      invalidatesTags: ['Capture', 'Review', 'Activity'],
    }),
    rejectCapture: b.mutation<{ status: CaptureStatus }, { id: string; reason?: string }>({
      query: ({ id, reason }) => ({
        url: `/capture/${encodeURIComponent(id)}/reject`, method: 'POST',
        body: reason ? { reason } : {},
      }),
      invalidatesTags: ['Capture', 'Review', 'Activity'],
    }),

    intakeReviewQueue: b.query<
      { items: IntakeReviewItem[]; crmWriteEnabled: boolean }, void
    >({
      query: () => '/review/intake', providesTags: ['Review'],
    }),
    extractCard: b.mutation<
      { capture: CardCaptureView; draft: CardDraft; cost: { modelId: string; estimatedCostMicros: number } },
      string
    >({
      query: (id) => ({ url: `/cards/${id}/extract`, method: 'POST' }),
      invalidatesTags: ['Activity', 'Connections', 'Review'],
    }),
    reviewCard: b.mutation<CardReviewResult, { id: string; body: Record<string, unknown> }>({
      query: ({ id, body }) => ({ url: `/cards/${id}/review`, method: 'PUT', body }),
      invalidatesTags: ['Activity', 'Review'],
    }),
    confirmCard: b.mutation<
      { capture: CardCaptureView; externalContactId: string | null; executed: boolean; mutatedCrm: boolean },
      { id: string; planHash: string }
    >({
      query: ({ id, planHash }) => ({ url: `/cards/${id}/confirm`, method: 'POST', body: { planHash } }),
      invalidatesTags: ['Activity', 'Overview', 'Review'],
    }),
    cancelCard: b.mutation<void, string>({
      query: (id) => ({ url: `/cards/${id}/cancel`, method: 'POST' }),
      invalidatesTags: ['Activity', 'Review'],
    }),
    connectAnthropic: b.mutation<
      { connector: Connector },
      { apiKey: string; anthropicWorkspaceId?: string | null }
    >({
      query: (body) => ({ url: '/connections/anthropic', method: 'POST', body }),
      invalidatesTags: ['Connections', 'Activity'],
    }),
    removeAnthropicKey: b.mutation<{ connector: Connector }, void>({
      query: () => ({ url: '/connections/anthropic', method: 'DELETE' }),
      invalidatesTags: ['Connections', 'Activity'],
    }),

    evaluateContact: b.mutation<Evaluation, {
      givenName?: string; familyName?: string; email?: string;
      phone?: string; company?: string; jobTitle?: string;
    }>({
      query: (body) => ({ url: '/contacts/evaluate', method: 'POST', body }),
      invalidatesTags: ['Activity'],
    }),
  }),
});

export const {
  useCompanionConnectionsQuery, useRevokeCompanionMutation, useAuthorizeCompanionMutation,
  useDestinationsQuery, useCreateWebhookMutation, useUpdateDestinationMutation, useRevokeDestinationMutation,
  useRotateDestinationSecretMutation, useTestDestinationMutation, useRetryDeliveryMutation,
  useCreateZapierKeyMutation, useRevokeZapierKeyMutation,
  useMeQuery, useWorkspaceQuery, useWorkspacesQuery, useLoginMutation, useLogoutMutation,
  useSwitchWorkspaceMutation, useConnectionsQuery, useConnectKeapMutation, useVerifyKeapMutation,
  useKeapTagsQuery, useTagMappingsQuery, useSaveTagMappingMutation, useOverviewQuery, useUsageQuery,
  useTagAssociationsQuery, useSaveTagAssociationMutation,
  useActivityQuery, useApproveTagMutation, useWithdrawTagMutation,
  useEvaluateContactMutation, useUploadCardMutation, useExtractCardMutation,
  useOpenIntakeBatchMutation, useIntakeBatchQuery, useIntakeBatchesQuery,
  useCancelIntakeBatchMutation, useResumeIntakeBatchMutation,
  useReviewCountQuery, useIntakeReviewQueueQuery, useReviewItemQuery,
  useMessageViewQuery, useSaveLabelFeedbackMutation,
  useUploadCsvMutation, useConfirmCsvMappingMutation,
  useIntakeSettingsQuery, useSaveIntakeSettingsMutation, useSaveIntakeDefaultTagsMutation,
  useCrmTagsQuery,
  useIntakeItemQuery, useEditIntakeItemMutation, useDecideIntakeItemMutation, useRecheckIntakeItemMutation,
  useBulkIntakeActionMutation,
  useReviewCardMutation, useConfirmCardMutation, useCancelCardMutation, useConnectAnthropicMutation, useRemoveAnthropicKeyMutation,
  useStartGmailMutation, useDisconnectGmailMutation, useSyncGmailMutation,
  useStartMicrosoftMutation, useDisconnectMicrosoftMutation, useSyncMicrosoftMutation,
  useDeleteHistoricalMailboxMutation,
  useRelationshipTypesQuery, useCreateRelationshipTypeMutation,
  useUpdateRelationshipTypeMutation,
  useReviewQueueQuery, useClassifiedMessagesQuery,
  useApproveReviewMutation, useRejectReviewMutation,
  useNoActionReviewMutation, useEditReviewTagsMutation, useCorrectRelationshipMutation,
  useCaptureSettingsQuery, useSaveCaptureSettingsMutation, useCaptureMarkersQuery,
  useSaveCaptureMarkerMutation, useMapCaptureMarkerMutation, useCreateCaptureMarkerMutation,
  useUpdateCaptureMarkerMutation,
  useSetCaptureMarkerEnabledMutation, useRemoveCaptureMarkerMutation, useCaptureQueueQuery,
  useCaptureMetricsQuery, useCaptureQuery, useReviewItemsQuery, useCardQuery,
  useEditCaptureMutation, useApproveCaptureMutation, useRetryCaptureMutation, useReopenCaptureMutation,
  useRejectCaptureMutation, useCrmContactTypesQuery, useConversationQuery,
  useEditConversationMutation, useApproveConversationMutation, useRejectConversationMutation,
  useRetryConversationMutation, useReopenConversationMutation,
  useContactDefaultsQuery, useSaveContactDefaultsMutation, useUpdateContactTypeMutation,
} = api;
